import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from 'react';
import moment from 'moment';
import { DailyNewsSummaryItem } from '@/types/api';
import { getDailyNewsSummary } from '@/api/newsApi';
import { useAppSelector } from '@/redux/store';
import { NewsErrorFallback } from '../_components/NewsErrorFallback';

interface NewsBriefingContextType {
  data: DailyNewsSummaryItem | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => void;
}

const NewsBriefingContext = createContext<NewsBriefingContextType | null>(null);

export const useNewsBriefing = () => {
  const context = useContext(NewsBriefingContext);
  if (!context) throw new Error('useNewsBriefing must be used within NewsBriefingProvider');
  return context;
};

export const NewsBriefingProvider = ({ children }: { children: ReactNode }) => {
  const [data, setData] = useState<DailyNewsSummaryItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(moment().format('YYYY-MM-DD'));

  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const user = useAppSelector((state) => state.user);

  const fetchNews = useCallback(async () => {
    setError(null);
    try {
      setIsLoading(true);
      const today = moment().format('YYYY-MM-DD');
      const result = await getDailyNewsSummary({ date: today });
      // setData(result[0]);
      // setCurrentDate(today);
      //TODO: 백단에서 날짜가 제대로 들어오는걸 확인 후 주석 해제

      if (result && result[0]) {
        const todayKSTISOString = moment().startOf('day').format('YYYY-MM-DDT00:00:00+09:00');

        const patchedResult = {
          ...result[0],
          date: todayKSTISOString, // 날짜 수정
        };

        setData(patchedResult);
        setCurrentDate(today);
      }
    } catch (error) {
      console.error('뉴스 데이터 로드 실패', error);
      setError('뉴스 데이터를 불러오지 못했습니다.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // 최초 로딩
  useEffect(() => {
    if (!user.UserNo || !user.CplatToken) return;
    fetchNews();
  }, [user.UserNo, user.CplatToken, fetchNews]);

  // 동적 인터벌 설정
  useEffect(() => {
    const checkAndUpdate = () => {
      const now = moment().format('YYYY-MM-DD');
      if (now !== currentDate) {
        fetchNews();
      }
    };

    const getNextInterval = () => {
      const now = moment();
      const tomorrow6AM = moment().startOf('day').add(1, 'day').hour(6);
      const diffMinutes = tomorrow6AM.diff(now, 'minutes');

      // 60분 초과 시 1시간마다, 이하일 때는 1분마다 체크
      return diffMinutes > 60 ? 60 * 60 * 1000 : 60 * 1000;
    };

    // 초기 등록
    const interval = setInterval(checkAndUpdate, getNextInterval());
    intervalRef.current = interval;

    // cleanup
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [currentDate, fetchNews]);

  if (error) {
    return <NewsErrorFallback onRetry={fetchNews} isLoading={isLoading} />;
  }

  return (
    <NewsBriefingContext.Provider value={{ data, isLoading, error, refetch: fetchNews }}>
      {children}
    </NewsBriefingContext.Provider>
  );
};
